// pages/ChangerNiveau.jsx
// Page pour changer le niveau par langue

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp, CheckCircle, Loader2, Globe } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import './ChangerNiveau.css';

export default function ChangerNiveau() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [niveaux, setNiveaux] = useState({
    fr: 1,
    en: 1,
    es: 1,
  });
  const [selectedNiveaux, setSelectedNiveaux] = useState({
    fr: 1,
    en: 1,
    es: 1,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // null ou 'fr', 'en', 'es'
  const [message, setMessage] = useState(null);

  // Définition des langues
  const langues = [
    { code: 'fr', label: t('langues.fr'), flag: '🇫🇷' },
    { code: 'en', label: t('langues.en'), flag: '🇬🇧' },
    { code: 'es', label: t('langues.es'), flag: '🇪🇸' },
  ];

  // Définition des niveaux avec descriptions des niveaux CECRL (6 niveaux)
  const niveauxDef = [
    {
      value: 1,
      label: t('changerNiveau.niveau1Label'),
      cecrl: 'A1',
      description: t('changerNiveau.niveau1Desc'),
    },
    {
      value: 2,
      label: t('changerNiveau.niveau2Label'),
      cecrl: 'A2',
      description: t('changerNiveau.niveau2Desc'),
    },
    {
      value: 3,
      label: t('changerNiveau.niveau3Label'),
      cecrl: 'B1',
      description: t('changerNiveau.niveau3Desc'),
    },
    {
      value: 4,
      label: t('changerNiveau.niveau4Label'),
      cecrl: 'B2',
      description: t('changerNiveau.niveau4Desc'),
    },
    {
      value: 5,
      label: t('changerNiveau.niveau5Label'),
      cecrl: 'C1',
      description: t('changerNiveau.niveau5Desc'),
    },
    {
      value: 6,
      label: t('changerNiveau.niveau6Label'),
      cecrl: 'C2',
      description: t('changerNiveau.niveau6Desc'),
    },
  ];

  // Charger les niveaux actuels pour toutes les langues
  useEffect(() => {
    const loadNiveaux = async () => {
      try {
        const response = await fetch(`/api/users/${user.id}/niveaux`);
        const data = await response.json();

        if (data.success) {
          const niveauxData = {};
          data.niveaux.forEach((n) => {
            niveauxData[n.langue] = n.niveau;
          });

          setNiveaux(niveauxData);
          setSelectedNiveaux(niveauxData);
        }
      } catch (error) {
        console.error('Erreur chargement niveaux:', error);
        setMessage({
          type: 'error',
          text: t('changerNiveau.erreurChargement'),
        });
      } finally {
        setLoading(false);
      }
    };

    loadNiveaux();
  }, []);

  // Sauvegarder le nouveau niveau pour une langue
  const handleSave = async (langue) => {
    setSaving(langue);
    setMessage(null);

    try {
      const response = await fetch(`/api/users/${user.id}/niveau/${langue}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ niveau: selectedNiveaux[langue] }),
      });

      const data = await response.json();

      if (data.success) {
        setNiveaux((prev) => ({ ...prev, [langue]: selectedNiveaux[langue] }));

        const langueLabel = langues.find((l) => l.code === langue)?.label || langue;
        const niveauLabel = niveauxDef.find((n) => n.value === selectedNiveaux[langue])?.cecrl || 'A1';

        setMessage({
          type: 'success',
          text: t('changerNiveau.niveauMisAJour', { langue: langueLabel, niveau: niveauLabel }),
        });
      } else {
        setMessage({
          type: 'error',
          text: t('changerNiveau.erreurSauvegarde'),
        });
      }
    } catch (error) {
      console.error('Erreur sauvegarde niveau:', error);
      setMessage({
        type: 'error',
        text: t('changerNiveau.erreurSauvegardeNiveau'),
      });
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="changer-niveau">
        <div className="loading-container">
          <Loader2 size={48} className="spinning" color="#4F46E5" />
          <p>{t('changerNiveau.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="changer-niveau">
      <div className="niveau-header">
        <Globe size={32} color="#4F46E5" />
        <h2>{t('changerNiveau.title')}</h2>
      </div>

      {/* Message global de succès/erreur */}
      {message && (
        <div className={`message ${message.type}`}>
          {message.type === 'success' && <CheckCircle size={20} />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Grille des 3 langues */}
      <div className="langues-grid">
        {langues.map((langue) => (
          <div key={langue.code} className="langue-card">
            {/* Header de la carte */}
            <div className="langue-card-header">
              <span className="langue-flag">{langue.flag}</span>
              <h3>{langue.label}</h3>
            </div>

            {/* Niveau actuel */}
            <div className="current-niveau-badge">
              <TrendingUp size={20} />
              <div>
                <div className="niveau-number">{t('profile.niveauLabel', { niveau: niveaux[langue.code] })}</div>
                <div className="niveau-label">
                  {niveauxDef.find((n) => n.value === niveaux[langue.code])?.label || t('changerNiveau.nonDefini')} (
                  {niveauxDef.find((n) => n.value === niveaux[langue.code])?.cecrl || 'A1'})
                </div>
              </div>
            </div>

            {/* Sélecteur de niveau */}
            <div className="niveau-select-wrapper">
              <label>{t('changerNiveau.choisirNouveauNiveau')}</label>
              <select
                value={selectedNiveaux[langue.code]}
                onChange={(e) =>
                  setSelectedNiveaux((prev) => ({
                    ...prev,
                    [langue.code]: parseInt(e.target.value),
                  }))
                }
                className="niveau-select"
              >
                {niveauxDef.map((niv) => (
                  <option key={niv.value} value={niv.value}>
                    {t('changerNiveau.niveauOption', { value: niv.value, label: niv.label, cecrl: niv.cecrl })}
                  </option>
                ))}
              </select>
              <p className="niveau-description">{niveauxDef.find((n) => n.value === selectedNiveaux[langue.code])?.description || ''}</p>
            </div>

            {/* Bouton de sauvegarde */}
            {selectedNiveaux[langue.code] !== niveaux[langue.code] && (
              <button onClick={() => handleSave(langue.code)} disabled={saving === langue.code} className="btn-save">
                {saving === langue.code ? (
                  <>
                    <Loader2 size={20} className="spinning" />
                    {t('changerNiveau.sauvegarde')}
                  </>
                ) : (
                  <>
                    <CheckCircle size={20} />
                    {t('changerNiveau.validerNiveau', { niveau: selectedNiveaux[langue.code] })}
                  </>
                )}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Informations CECRL */}
      <div className="info-card">
        <h4>{t('changerNiveau.cecrlTitre')}</h4>
        <p>
          {t('changerNiveau.cecrlIntroBefore')} <strong>{t('changerNiveau.cecrlIntroStrong')}</strong> {t('changerNiveau.cecrlIntroAfter')}
        </p>
        <ul>
          <li>
            <strong>{t('changerNiveau.a1a2Label')}</strong> : {t('changerNiveau.a1a2Desc')}
          </li>
          <li>
            <strong>{t('changerNiveau.b1b2Label')}</strong> : {t('changerNiveau.b1b2Desc')}
          </li>
          <li>
            <strong>{t('changerNiveau.c1c2Label')}</strong> : {t('changerNiveau.c1c2Desc')}
          </li>
        </ul>
        <p className="info-note">{t('changerNiveau.infoNote')}</p>
      </div>
    </div>
  );
}
